import { db, firebaseConfig, auth } from "../firebase/config";
import { collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot, getDoc, setDoc, query, where, getDocs } from "firebase/firestore";
import { initializeApp, getApp, getApps } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail } from "firebase/auth";
import { logSecurityEvent } from "./auditLogger";

// Setup secondary auth for user management safely
const secondaryApp = getApps().find(app => app.name === "secondary") || initializeApp(firebaseConfig, "secondary");
const secondaryAuth = getAuth(secondaryApp);

export function subscribeToUsers(callback) {
  return onSnapshot(collection(db, "users"), (snapshot) => {
    const list = [];
    snapshot.forEach((doc) => {
      list.push({ id: doc.id, ...doc.data() });
    });
    // Sort by name if name exists
    list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    callback(list);
  }, (error) => {
    console.error("Error subscribing to users:", error);
    callback([], error);
  });
}

export async function addUser(userData) {
  const { password, ...firestoreData } = userData;
  let authUid = "";

  if (password && firestoreData.email) {
    try {
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, firestoreData.email, password);
      authUid = userCredential.user.uid;
      // Immediately sign them out of the secondary auth instance to prevent state leak
      await signOut(secondaryAuth);
    } catch (err) {
      console.error("Auth creation failed:", err);
      throw new Error(`Failed to create Authentication account: ${err.message}`);
    }
  }

  // Save the user doc in Firestore using authUid if available, otherwise auto-ID
  let docRef;
  const finalData = { ...firestoreData, ...(authUid ? { uid: authUid } : {}) };
  
  if (authUid) {
    docRef = doc(db, "users", authUid);
    await setDoc(docRef, finalData);
    return authUid;
  } else {
    const newDoc = await addDoc(collection(db, "users"), finalData);
    return newDoc.id;
  }
}

export async function updateUser(id, userData) {
  const docRef = doc(db, "users", id);
  await updateDoc(docRef, userData);
}

export async function deleteUser(id) {
  const docRef = doc(db, "users", id);
  await updateDoc(docRef, {
    deleted: true,
    status: "Disabled"
  });
}

export async function getUserPreferences(uid) {
  if (!uid) return null;
  const docRef = doc(db, "user_preferences", uid);
  const snap = await getDoc(docRef);
  if (snap.exists()) {
    return snap.data();
  }
  return null;
}

export async function saveUserPreferences(uid, prefs) {
  if (!uid) return;
  const docRef = doc(db, "user_preferences", uid);
  await setDoc(docRef, prefs, { merge: true });
}

export async function enableUserMFA(id, { secret, backupCodes }) {
  const docRef = doc(db, "users", id);
  await updateDoc(docRef, {
    mfaEnabled: true,
    mfaSecret: secret,
    mfaBackupCodes: backupCodes || [],
    mfaEnrolledAt: new Date().toISOString()
  });
}

export async function disableUserMFA(id) {
  const docRef = doc(db, "users", id);
  await updateDoc(docRef, {
    mfaEnabled: false,
    mfaSecret: null,
    mfaBackupCodes: [],
    mfaDisabledAt: new Date().toISOString()
  });
}

export async function updateUserBackupCodes(id, backupCodes) {
  const docRef = doc(db, "users", id);
  await updateDoc(docRef, {
    mfaBackupCodes: backupCodes || []
  });
}

export async function resetUserPassword(email, requesterEmail = "Admin") {
  if (!email || !email.trim()) {
    throw new Error("User email is required to send password reset link.");
  }
  try {
    await sendPasswordResetEmail(auth, email.trim());
    await logSecurityEvent({
      action: "AUTH_PASSWORD_RESET_REQUESTED",
      category: "AUTH",
      userEmail: requesterEmail,
      resource: `user:${email.trim()}`,
      status: "SUCCESS",
      details: {
        targetEmail: email.trim(),
        initiatedBy: requesterEmail
      },
      severity: "MEDIUM"
    });
    return true;
  } catch (err) {
    console.error("Failed to send password reset email:", err);
    await logSecurityEvent({
      action: "AUTH_PASSWORD_RESET_FAILED",
      category: "AUTH",
      userEmail: requesterEmail,
      resource: `user:${email.trim()}`,
      status: "FAILURE",
      details: {
        targetEmail: email.trim(),
        error: err.message
      },
      severity: "HIGH"
    });
    throw err;
  }
}

/**
 * Updates the 'lastLogin' and 'lastLoggedIn' timestamp for a user in Firestore.
 * Supports updating by UID, or querying by email.
 */
export async function recordUserLogin(user) {
  if (!user || (!user.uid && !user.email)) return;
  try {
    const nowIso = new Date().toISOString();
    let updated = false;

    // 1. Try directly by user.uid
    if (user.uid) {
      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        await updateDoc(userRef, {
          lastLogin: nowIso,
          lastLoggedIn: nowIso
        });
        updated = true;
      }
    }

    // 2. If not updated by UID, locate user by email
    if (!updated && user.email) {
      const emailLower = user.email.toLowerCase();
      const q = query(collection(db, "users"), where("email", "==", emailLower));
      const snap = await getDocs(q);
      if (!snap.empty) {
        await updateDoc(doc(db, "users", snap.docs[0].id), {
          lastLogin: nowIso,
          lastLoggedIn: nowIso
        });
        updated = true;
      } else if (user.email !== emailLower) {
        const q2 = query(collection(db, "users"), where("email", "==", user.email));
        const snap2 = await getDocs(q2);
        if (!snap2.empty) {
          await updateDoc(doc(db, "users", snap2.docs[0].id), {
            lastLogin: nowIso,
            lastLoggedIn: nowIso
          });
          updated = true;
        }
      }
    }
  } catch (err) {
    console.warn("[userService] Failed to record user last login:", err.message);
  }
}


